/**
 * An amount and the words that say which way it points.
 *
 * There is one of these and every balance on both screens goes through it,
 * which is how the rule holds: **the direction is always written out**. The
 * `direction` decides the class *and* the label together, from the same
 * value, so a colour cannot end up saying something the words do not. Strip
 * the stylesheet and the screen still reads correctly; read it in greyscale,
 * through a screen reader, or on a printout and it still reads correctly.
 *
 * The arrow is a second non-colour cue for the same fact, and it is
 * `aria-hidden` because the label beside it already says what it means.
 *
 * ## The counting, and the confetti
 *
 * It takes the cents rather than the digits, because the digits are what it
 * counts *to*: `useCountingMoney` writes the number on its way to the value
 * over 600ms and finishes on exactly the string this component rendered, so
 * nothing downstream of it can end up out of step with the words. The label,
 * the sentence and the screen reader's version are all the final value from
 * the first frame — a number that is still moving is a decoration, and it is
 * never the thing anybody is told.
 *
 * A balance that *arrives* at zero is the one moment this app celebrates: the
 * row flashes, the digits run down to nought, and the confetti goes off. It
 * needs a real transition to fire — being settled up already is not news, so
 * a screen that opens on a settled row opens quietly.
 */

import { formatMoney, type Direction } from '../lib/format'
import { useCountingMoney } from '../motion-react'
import { Confetti } from './Confetti'

const GLYPH: Record<Direction, string> = {
  incoming: '↑',
  outgoing: '↓',
  settled: '·',
}

export function Amount({
  direction,
  label,
  cents,
  currency,
  size = 'row',
  count = true,
}: {
  direction: Direction
  label: string
  /** The integer the digits are made of — signed, as the balance is. */
  cents: number
  currency: string
  size?: 'row' | 'headline'
  /**
   * False for the numbers that are history rather than news. What one expense
   * did to you last April is not counting to anything, and forty rows of
   * digits spinning at once on the way down a list is a slot machine.
   */
  count?: boolean
}) {
  // An amount at zero is `—` rather than `$0.00`: "settled up" is already
  // written beside it, and a zero is a number somebody reads as a balance.
  const final = direction === 'settled' ? '—' : formatMoney(cents, currency)
  const { ref, settling } = useCountingMoney({ cents, currency, final, count })

  return (
    <span
      className={
        `amount amount--${direction} amount--${size}` + (settling ? ' amount--settling' : '')
      }
    >
      <span className="amount__label">
        <span aria-hidden="true" className="amount__glyph">
          {GLYPH[direction]}
        </span>
        {label}
      </span>
      <span className="amount__value" ref={ref}>
        {final}
      </span>
      {settling && <Confetti />}
    </span>
  )
}

/** The same pairing, laid out for a chip rather than a row. */
export function AmountChip({
  direction,
  label,
  cents,
  currency,
  name,
}: {
  direction: Direction
  label: string
  cents: number
  currency: string
  name: string
}) {
  /**
   * Empty rather than absent when there is nothing to show. A settled chip has
   * no amount on it — that is what `.chip__value:empty` hides — but the
   * element has to exist for the digits to be able to count *down* into it on
   * the way to zero, and a chip that grew a new box halfway through the count
   * would be the layout movement this whole pass is trying not to make.
   */
  const final = direction === 'settled' ? '' : formatMoney(cents, currency)
  const { ref, settling } = useCountingMoney({ cents, currency, final })

  return (
    <span className={`chip chip--${direction}` + (settling ? ' chip--settling' : '')}>
      <span className="chip__name">{name}</span>
      <span className="chip__label">{label}</span>
      <span className="chip__value" ref={ref}>
        {final}
      </span>
      {settling && <Confetti />}
    </span>
  )
}
